import { UAParser } from 'ua-parser-js'
import { LocalInsert } from '@weblite/persist'
import { SESSION_STORAGE_KEYS } from '$logic/client-device/types'
import {
  sessionStorageGetItem,
  sessionStorageSetItem,
} from '$func/session-storage'

const isInTwa = () => {
  try {
    const wasTwa = sessionStorageGetItem(SESSION_STORAGE_KEYS.IS_TWA)
    if (wasTwa != null) return wasTwa
    // eslint-disable-next-line no-empty
  } catch (e) {}

  const params = new URLSearchParams(window.location.search)

  const twaPort = params.get('twa_port')
  if (twaPort) sessionStorageSetItem(SESSION_STORAGE_KEYS.TWA_PORT, twaPort)

  const twaWsPort = params.get('twa_ws_port')
  const twaWsToken = params.get('twa_ws_token') || ''
  if (twaWsPort) {
    sessionStorageSetItem(
      SESSION_STORAGE_KEYS.TWA_WS_URL,
      `ws://127.0.0.1:${twaWsPort}/${twaWsToken}`,
    )
  }

  const isTWA =
    document.referrer.includes('android-app://') ||
    params.get('utm_source') === 'twa' ||
    params.get('twa_version') != null
  sessionStorageSetItem(SESSION_STORAGE_KEYS.IS_TWA, String(isTWA))

  if (isTWA) LocalInsert('twaVersion', parseInt(params.get('twa_version')!, 10))
  return isTWA
}

const isNumber = (x: string) => /^\d+$/.test(x)

/**
 * => null versions can not be compared
 * => 1 if first > second
 * => -1 if second > first
 * => 0 if first === second
 */
const compare = (first: string, second: string) => {
  const v1 = first
    .split('.')
    .filter(isNumber)
    .map(Number)
  const v2 = second
    .split('.')
    .filter(isNumber)
    .map(Number)

  if (v1.length === 0 || v2.length === 0) return null

  // Zero padding
  while (v1.length < v2.length) v1.push(0)
  while (v2.length < v1.length) v2.push(0)

  // compare
  for (let i = 0; i < v1.length; ++i) {
    if (v1[i] === v2[i]) continue
    else if (v1[i] > v2[i]) return 1
    else return -1
  }

  return 0
}

interface ErrorPageUrl {
  url: string
  search?: string
}

// not supported version
export const lt = (first: string, second: string) =>
  compare(first, second) === -1

enum OsType {
  iOS = 'iOS',
  Windows = 'Windows',
  Mac = 'Mac OS',
  Android = 'Android',
  Linux = 'linux',
  Other = 'other',
}

enum BrowserName {
  Chrome = 'Chrome',
  Chromium = 'Chromium',
  Firefox = 'Firefox',
  Mozilla = 'Mozilla',
  Edge = 'Edge',
  Safari = 'Safari',
  MobileSafari = 'Mobile Safari',
  SamsungBrowser = 'Samsung Browser',
}

//  @ts-expect-error there is a chance that opera browser don't have window.opera
const UA = navigator.userAgent || navigator.vendor || window.opera
const parser = new UAParser(UA)

const { name: OS_NAME } = parser.getOS()
const { name: BROWSER, version: BROWSER_VERSION } = parser.getBrowser()

const isLinux = new Set(['Ubuntu', 'Mint', 'Linux', 'Debian', 'Fedora']).has(
  OS_NAME || '',
)

const isAndroid = OS_NAME === OsType.Android

const OS_TYPE = (() => {
  if (!OS_NAME) return 'other' as OsType

  if (new Set(['iOS', 'Mac OS', 'Windows', 'Android']).has(OS_NAME))
    return OS_NAME as OsType

  if (isLinux) return 'linux' as OsType

  return 'other' as OsType
})()

export const minimumBrowserRequirements: Record<
  OsType,
  Partial<Record<BrowserName, string>>
> = {
  iOS: {
    'Mobile Safari': '15.6',
    Chrome: '109',
    Firefox: '115',
    Edge: '109',
  },
  'Mac OS': {
    Safari: '16.6',
    Chrome: '109',
    Firefox: '115',
    Edge: '109',
  },
  Windows: {
    Chrome: '109',
    Chromium: '109',
    Firefox: '115',
    Mozilla: '115',
    Edge: '109',
  },
  Android: {
    'Samsung Browser': '20',
    Chrome: '109',
    Firefox: '115',
    Mozilla: '115',
    Edge: '109',
  },
  linux: {
    Chrome: '109',
    Chromium: '109',
    Firefox: '115',
    Mozilla: '115',
    Edge: '109',
  },
  other: {
    Chrome: '109',
    Edge: '109',
    Firefox: '115',
    'Mobile Safari': '15.6',
    Safari: '16.6',
  },
}

const isIOS =
  OS_NAME === OsType.iOS ||
  (window.orientation !== undefined && OS_NAME === 'Mac OS') // for iPadOS

const isMacOS = OS_NAME === OsType.Mac
export const isSupportedBrowser = () => {
  if (!BROWSER) return false

  return BROWSER in minimumBrowserRequirements[OS_TYPE]
}

export const getOutDatedBrowserMeta = () => {
  const browser = BROWSER as BrowserName

  const version = minimumBrowserRequirements[OS_TYPE][browser]
  if (browser && version && BROWSER_VERSION)
    return {
      clientBrowser: browser,
      clientBrowserVersion: BROWSER_VERSION,
      recommendedBrowserVersion: version,
    }
  return null
}

export const isOutDatedBrowser = () => {
  if (!BROWSER || !BROWSER_VERSION) return true
  const browser = BROWSER as BrowserName
  const osType = isIOS ? OsType.iOS : OS_TYPE
  if (minimumBrowserRequirements[osType][browser])
    return lt(BROWSER_VERSION, minimumBrowserRequirements[OS_TYPE][browser]!)
  return true
}

// https://github.com/dvlden/is-webview
const checkIsWebView = (userAgent: string) => {
  return /webview|wv|ip((?!.*Safari)|(?=.*like Safari))/i.test(userAgent)
}

export const isWebView = checkIsWebView(UA)

// const isCriticalError = (error: Error) => {
//   return (
//     error instanceof ReferenceError ||
//     error instanceof TypeError ||
//     error instanceof DOMException
//   )
// }

const isCookieEnabled = () => {
  if (navigator.cookieEnabled) return true
  document.cookie = 'cookietest=1'
  const ret = document.cookie.indexOf('cookietest=') !== -1
  document.cookie = 'cookietest=1; expires=Thu, 01-Jan-1970 00:00:01 GMT'
  return ret
}

const ERROR_PAGES_DIR_PATH = 'error-pages'
const ERROR_PAGES_PATHS = {
  cookieDisabled: 'cookie-disabled.html',
  installTWA: 'install-twa.html',
  isWebView: 'web-view.html',
  browserNotSupported: 'browser-not-supported.html',
  outdatedBrowser: 'out-dated-browser.html',
}

function getBrowserCompatibilityErrorPageUrl(): ErrorPageUrl | undefined {
  if (!isSupportedBrowser()) {
    return {
      url: [ERROR_PAGES_DIR_PATH, ERROR_PAGES_PATHS.browserNotSupported].join(
        '/',
      ),
      search: new URLSearchParams({
        isIOS: String(isIOS || isMacOS),
        isTWA: String(Boolean(isInTwa())),
      }).toString(),
    }
  }
  if (isOutDatedBrowser()) {
    const outDatedBrowserMeta = getOutDatedBrowserMeta()

    if (outDatedBrowserMeta) {
      const {
        clientBrowser,
        clientBrowserVersion,
        recommendedBrowserVersion,
      } = outDatedBrowserMeta
      return {
        url: [ERROR_PAGES_DIR_PATH, ERROR_PAGES_PATHS.outdatedBrowser].join(
          '/',
        ),
        search: new URLSearchParams({
          clientBrowser,
          clientBrowserVersion,
          recommendedBrowserVersion,
        }).toString(),
      }
    }
  }
  return undefined
}

export function getErrorPageUrl(): ErrorPageUrl | undefined {
  if (!isCookieEnabled()) {
    return {
      url: [ERROR_PAGES_DIR_PATH, ERROR_PAGES_PATHS.cookieDisabled].join('/'),
    }
  }
  if (isWebView && (isAndroid || isIOS)) {
    return {
      url: [ERROR_PAGES_DIR_PATH, ERROR_PAGES_PATHS.isWebView].join('/'),
      search: new URLSearchParams({ isAndroid: String(isAndroid) }).toString(),
    }
  }
  return getBrowserCompatibilityErrorPageUrl()
}

export function calErrorPageUrl(errorPageUrl: ErrorPageUrl) {
  return `${errorPageUrl.url}?${errorPageUrl.search || ''}`
}

function checkError() {
  const errorPageUrl = getErrorPageUrl()
  if (errorPageUrl) {
    window.location.href = new URL(
      calErrorPageUrl(errorPageUrl),
      window.location.origin,
    ).toString()
  }
}

function checkBrowserCompatibility() {
  const errorPageUrl = getBrowserCompatibilityErrorPageUrl()
  if (errorPageUrl) {
    window.location.href = new URL(
      calErrorPageUrl(errorPageUrl),
      window.location.origin,
    ).toString()
  }
}

if (
  window.config.frontend.VITE_CHECK_BROWSER_COMPATIBILITY_ON_LOAD === 'true'
) {
  checkBrowserCompatibility()
}

window.addEventListener(
  'error',
  () => {
    checkError()
    return true
  },
  false,
)
